/**
 * State and run logic for the bulk licence assignment dialog (APD-13993).
 *
 * <p>Loads the CMEs and telephony systems, reads every page of a system's licence list and each
 * device's groups, tracks the filters and selection, and assigns one licence to each selected row in
 * turn. Requests are built by the page's own builders (see licenceAssignment.ts), so a bulk run sends
 * what assigning each row by hand would.</p>
 */
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LicenceManagementPageLanguage } from '../../../../../../i18n'
import { ReportingAccessLevels } from '../../../../../NonReact/constants/Permissions'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../../React/Common/Constants/Separators'
import { ALL_VALUE } from '../../../../../React/Common/Constants/DefaultValues'
import {
    getCMEsAsSelectOptions,
    getLicenceManagementList,
    getTelephonyServer,
    getTelServersAsSelectOptionsWithCXIFilter
} from '../../../../../React/Common/API/ItemsGetters'
import { getTelSysLicences } from '../../../../../React/Common/API/Licence/LicenceAPI'
import {
    createUser,
    DeviceOrAgentGroupMembershipRow,
    getDeviceGroupMemberships,
    getUser,
    updateUser
} from '../../../../../NonReact/common/API/EntityAPIClient'
import { SelectOption, SelectOptions } from '../../../../../React/Common/Components/Selects/Types'
import { IndividualTelItem } from '../../../../../React/Common/Types/Types'
import { LicenceResponseMapped } from '../../../../../NonReact/Types/Types'
import { AdminUserBody } from '../../../../../NonReact/common/Types/Types'
import { ApplicationUser } from '../../../../../NonReact/common/Types/AddUser/AddUserTypes'
import {
    LicenceManagementListItem,
    LicenceManagementListResponse
} from '../../../../../NonReact/Types/LicenceManagementPageTypes'
import {
    HISTORIC_ONLY,
    INTEGRATION_MODE,
    INTEGRATION_TYPE as INTEGRATION_TYPE_FIELD
} from '../../../../../NonReact/constants/TelSysConstants'
import { IntegrationType } from '../../../../../React/TelSysConfiguration/Constants/Constants'
import {
    buildCreateUserBody,
    buildExistingUserLicenceUpdate,
    LicenceDraftValues,
    mapListItemToRow,
    normaliseTelSysIntegrationType,
    seedRowLicenceValues
} from '../licenceAssignment'
import {
    BULK_LICENCE_PROFILES,
    BulkLicence,
    BulkLicenceProfile,
    bulkLicenceLabel,
    BulkProfileId,
    resolveBulkProfile,
    ResolvedBulkProfile
} from './bulkLicenceProfiles'
import {
    adminUserError,
    BlockerCode,
    BULK_LIST_PAGE_SIZE,
    BulkError,
    BulkGroupSummary,
    BulkRow,
    bulkDisplayName,
    bulkUsername,
    buildGroupSummaries,
    describeBulkError,
    errorStatus,
    getBulkBlocker,
    GROUP_LOAD_CONCURRENCY,
    isAdminUser,
    isItemLicensed,
    isPartialResult,
    isItemOnTelSys,
    isSavedAsSent,
    isServerError,
    isSessionLoss,
    matchesBulkSearch,
    prefilledEmailFor,
    runWithConcurrency
} from './bulkAssignRules'
import { buildResultsCsv, BulkResultLine, downloadCsv, resultsCsvFileName } from './bulkResultsCsv'

export const BulkRowStatusKind = {
    WAITING: 'waiting',
    WORKING: 'working',
    DONE: 'done',
    DONE_WARNING: 'doneWarning',
    FAILED: 'failed',
    NOT_RUN: 'notRun'
} as const

export type BulkRowStatusKind = (typeof BulkRowStatusKind)[keyof typeof BulkRowStatusKind]

/** Outcome of the latest run for a row. */
export type BulkRowStatus = {
    readonly kind: BulkRowStatusKind
    readonly text: string
    readonly detail?: BulkError
    /** Licence the run assigned (or checked). */
    readonly licence: BulkLicence
}

/** Live tally of the run in progress (and of the last run, until its summary is closed). */
export type BulkRunCounts = {
    readonly total: number
    readonly succeeded: number
    readonly failed: number
}

export type BulkRunSummary = {
    readonly succeeded: number
    readonly failed: number
    readonly stopped: boolean
}

/** What the confirm dialog shows before a real run. */
export type BulkRunPlan = {
    readonly total: number
    readonly newUsers: number
    readonly existingUsers: number
    /** Licences of this type still available on the system, when known. */
    readonly remaining: Nullable<number>
    /** Selected users beyond the remaining licences, who will fail. 0 when capacity is unknown or enough. */
    readonly shortfall: number
}

export type UseBulkAssignParams = {
    readonly language: CLanguageHandler
    readonly initialCmeId: string
    readonly initialTelSysId: Nullable<string | number>
    readonly isCxiDataEnabled: boolean
}

export type UseBulkAssign = {
    readonly cmeOptions: SelectOptions<string>
    readonly cmeId: string
    readonly telSysOptions: SelectOptions<string>
    readonly telSysId: string
    readonly profile: BulkLicenceProfile
    readonly systemNote: string
    readonly isDetected: boolean
    readonly rows: readonly BulkRow[]
    readonly visibleRows: readonly BulkRow[]
    readonly isLoadingUsers: boolean
    readonly isLoadingGroups: boolean
    /** True while the chosen system's type is being read; loading and licence choice wait for it. */
    readonly isResolvingType: boolean
    readonly loadMessage: string
    readonly groups: readonly BulkGroupSummary[]
    readonly selectedGroups: ReadonlySet<string>
    readonly groupSearch: string
    readonly search: string
    readonly showLicensed: boolean
    readonly licence: BulkLicence
    readonly welcomeEmail: boolean
    readonly isRunning: boolean
    readonly progress: number
    readonly runCounts: Nullable<BulkRunCounts>
    readonly isConfirmOpen: boolean
    readonly summary: Nullable<BulkRunSummary>
    readonly hasChanges: boolean
    readonly selectedRows: readonly BulkRow[]
    readonly allVisibleSelected: boolean
    readonly anyVisibleSelectable: boolean
    readonly hasResults: boolean
    readonly runPlan: BulkRunPlan
    readonly emailFor: (row: BulkRow) => string
    readonly nameFor: (row: BulkRow) => string
    readonly usernameFor: (row: BulkRow) => string
    readonly blockerFor: (row: BulkRow) => Nullable<BlockerCode>
    readonly isDone: (row: BulkRow) => boolean
    readonly isSelected: (row: BulkRow) => boolean
    readonly isSelectable: (row: BulkRow) => boolean
    readonly statusFor: (row: BulkRow) => Definable<BulkRowStatus>
    readonly licenceLabel: (licence: BulkLicence) => string
    readonly assignedLicence: (row: BulkRow) => Definable<BulkLicence>
    readonly selectCme: (cmeId: string) => void
    readonly selectTelSys: (telSysId: string) => void
    readonly loadUsers: () => Promise<void>
    readonly setSearch: (search: string) => void
    readonly setShowLicensed: (show: boolean) => void
    readonly setGroupSearch: (search: string) => void
    readonly toggleGroup: (groupID: string) => void
    readonly clearGroups: () => void
    readonly setLicence: (licence: BulkLicence) => void
    readonly setWelcomeEmail: (welcomeEmail: boolean) => void
    readonly toggleRow: (row: BulkRow) => void
    readonly setAllVisibleSelected: (selected: boolean) => void
    readonly setEmail: (row: BulkRow, email: string) => void
    readonly requestRun: () => void
    readonly confirmRun: () => void
    readonly cancelConfirm: () => void
    readonly stop: () => void
    readonly closeSummary: () => void
    readonly downloadResults: () => void
}

/** Maps a bulk licence to the matching available-licence count on the system summary. */
const remainingLicences = (summary: Nullable<LicenceResponseMapped>, licence: BulkLicence): Nullable<number> => {
    const available: Definable<LicenceResponseMapped['AvailableLicences']> = summary?.AvailableLicences

    if (!available) return null

    switch (licence) {
        case ReportingAccessLevels.ESSENTIALS:
            return available.Essentials
        case ReportingAccessLevels.PROFESSIONAL:
            return available.Professional
        default:
            return available.CallCentreAgent
    }
}

/** Errors are reported in the dialog itself, so a settled promise needs no further handling. */
const ignoreError = (): void => undefined

const isConcreteID = (value: Nullable<string | number>): boolean =>
    isVarNotEmpty(value) && String(value) !== EMPTY_STRING && String(value) !== String(ALL_VALUE)

const byLabel = <T extends string | number>(a: SelectOption<T>, b: SelectOption<T>): number =>
    String(a.label).localeCompare(String(b.label))

/**
 * Bulk assign state machine.
 *
 * @param params Language handler, the page's current CME and telsys, and the CXI Data flag.
 * @returns State and actions for the dialog.
 */
export const useBulkAssign = ({
    language,
    initialCmeId,
    initialTelSysId,
    isCxiDataEnabled
}: UseBulkAssignParams): UseBulkAssign => {
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const stopRef: MutableRefObject<boolean> = useRef<boolean>(false)
    const selectionTokenRef: MutableRefObject<number> = useRef<number>(0)
    const cmeTokenRef: MutableRefObject<number> = useRef<number>(0)

    const [cmeOptions, setCmeOptions] = useState<SelectOptions<string>>([])
    const [cmeId, setCmeId] = useState<string>(EMPTY_STRING)
    const [telSysOptions, setTelSysOptions] = useState<SelectOptions<string>>([])
    const [telSysId, setTelSysId] = useState<string>(EMPTY_STRING)
    const [telSysType, setTelSysType] = useState<Nullable<string>>(null)
    const [integrationType, setIntegrationType] = useState<Nullable<IntegrationType>>(null)
    const [profile, setProfile] = useState<BulkLicenceProfile>(BULK_LICENCE_PROFILES[BulkProfileId.BROADWORKS])
    const [systemNote, setSystemNote] = useState<string>(EMPTY_STRING)
    const [isDetected, setIsDetected] = useState<boolean>(false)
    const [licenceSummary, setLicenceSummary] = useState<Nullable<LicenceResponseMapped>>(null)
    const [rows, setRows] = useState<readonly BulkRow[]>([])
    const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false)
    const [isLoadingGroups, setIsLoadingGroups] = useState<boolean>(false)
    const [isResolvingType, setIsResolvingType] = useState<boolean>(false)
    const [loadMessage, setLoadMessage] = useState<string>(EMPTY_STRING)
    const [groupsByKey, setGroupsByKey] = useState<ReadonlyMap<string, readonly DeviceOrAgentGroupMembershipRow[]>>(
        new Map()
    )
    const [selectedGroups, setSelectedGroups] = useState<ReadonlySet<string>>(new Set())
    const [groupSearch, setGroupSearch] = useState<string>(EMPTY_STRING)
    const [search, setSearch] = useState<string>(EMPTY_STRING)
    const [showLicensed, setShowLicensed] = useState<boolean>(false)
    const [licence, setLicence] = useState<BulkLicence>(profile.defaultLicence)
    const [welcomeEmail, setWelcomeEmail] = useState<boolean>(false)
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
    const [emailOverrides, setEmailOverrides] = useState<ReadonlyMap<string, string>>(new Map())
    const [statuses, setStatuses] = useState<ReadonlyMap<string, BulkRowStatus>>(new Map())
    const [assigned, setAssigned] = useState<ReadonlyMap<string, BulkLicence>>(new Map())
    const [isRunning, setIsRunning] = useState<boolean>(false)
    const [progress, setProgress] = useState<number>(0)
    const [runCounts, setRunCounts] = useState<Nullable<BulkRunCounts>>(null)
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
    const [summary, setSummary] = useState<Nullable<BulkRunSummary>>(null)
    const [hasChanges, setHasChanges] = useState<boolean>(false)

    const licenceLabel = useCallback(
        (value: BulkLicence): string => bulkLicenceLabel(language, profile, value),
        [language, profile]
    )

    const resetSystemData = (): void => {
        setRows([])
        setGroupsByKey(new Map())
        setSelectedGroups(new Set())
        setSelected(new Set())
        setEmailOverrides(new Map())
        setStatuses(new Map())
        setAssigned(new Map())
        setLoadMessage(EMPTY_STRING)
    }

    const applyProfile = (resolved: ResolvedBulkProfile): void => {
        setProfile(resolved.profile)
        setLicence(resolved.profile.defaultLicence)
    }

    const profileName = (resolved: ResolvedBulkProfile, typeName: Nullable<string>): string => {
        if (!resolved.isKnownType) {
            return language.getInterpolated(
                LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_UnknownType,
                typeName ?? EMPTY_STRING
            )
        }

        switch (resolved.profile.id) {
            case BulkProfileId.TEAMS_BUSINESS:
                return language.get(
                    LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Profile_TeamsBusiness
                )
            case BulkProfileId.TEAMS_LEGACY:
                return language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Profile_TeamsLegacy)
            case BulkProfileId.WEBEX_CALLING:
                return language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Profile_Wxc)
            default:
                return language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Profile_BroadWorks)
        }
    }

    const chooseTelSys = async (id: string, detected: boolean, optionType?: Nullable<string>): Promise<void> => {
        selectionTokenRef.current += 1
        const token: number = selectionTokenRef.current

        setTelSysId(id)
        setIsDetected(detected)
        resetSystemData()
        setLicenceSummary(null)

        if (IsStringEmpty(id)) {
            setIsResolvingType(false)
            setSystemNote(language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_ChooseTelSys))
            return
        }

        setIsResolvingType(true)
        setSystemNote(language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_CheckingType))

        getTelSysLicences(id)
            .then((licences: Nullable<LicenceResponseMapped>): void => {
                if (isMounted.current && token === selectionTokenRef.current) setLicenceSummary(licences)
            })
            .catch((): void => {
                // Capacity is informational only.
            })

        try {
            const telSys: Nullable<IndividualTelItem> = normaliseTelSysIntegrationType(
                await getTelephonyServer(id, INTEGRATION_TYPE_FIELD, HISTORIC_ONLY, INTEGRATION_MODE)
            )

            if (!isMounted.current || token !== selectionTokenRef.current) return

            const type: Nullable<string> = telSys?.TelSysType ?? optionType ?? null
            const resolved: ResolvedBulkProfile = resolveBulkProfile(type, telSys?.IntegrationType)

            setTelSysType(type)
            setIntegrationType((telSys?.IntegrationType as Nullable<IntegrationType>) ?? null)
            applyProfile(resolved)
            setSystemNote(profileName(resolved, type))
            setIsResolvingType(false)
        } catch (error) {
            if (!isMounted.current || token !== selectionTokenRef.current) return

            setTelSysType(optionType ?? null)
            setIntegrationType(null)
            applyProfile({ profile: BULK_LICENCE_PROFILES[BulkProfileId.BROADWORKS], isKnownType: false })
            setIsResolvingType(false)
            setSystemNote(
                language.getInterpolated(
                    LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_TypeReadFailed,
                    describeBulkError(error, language).text
                )
            )
        }
    }

    const loadSystems = async (cme: string, preferId: Nullable<string>, detected: boolean): Promise<void> => {
        // A newer CME choice wins, and any type lookup for the old CME's telsys is dropped.
        cmeTokenRef.current += 1
        selectionTokenRef.current += 1
        const token: number = cmeTokenRef.current

        setTelSysOptions([])
        setTelSysId(EMPTY_STRING)
        setIsResolvingType(false)
        setLicenceSummary(null)
        resetSystemData()

        if (IsStringEmpty(cme)) {
            setSystemNote(language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_ChooseCmeFirst))
            return
        }

        try {
            const options: SelectOptions<number> = await getTelServersAsSelectOptionsWithCXIFilter(
                cme,
                true,
                true,
                false,
                ReportingAccessLevels.ENTERPRISE
            )

            if (!isMounted.current || token !== cmeTokenRef.current) return

            const mapped: SelectOptions<string> = [...options].sort(byLabel).map(
                (option: SelectOption<number>): SelectOption<string> => ({
                    label: isStringNotEmpty(option.telSysType)
                        ? `${option.label} (${option.telSysType})`
                        : option.label,
                    value: String(option.value),
                    telSysType: option.telSysType
                })
            )

            setTelSysOptions(mapped)

            const preferred: Definable<SelectOption<string>> = mapped.find(
                (option: SelectOption<string>): boolean => option.value === preferId
            )

            if (preferred) {
                await chooseTelSys(preferred.value, detected, preferred.telSysType)
            } else {
                setSystemNote(language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_ChooseTelSys))
            }
        } catch (error) {
            if (!isMounted.current || token !== cmeTokenRef.current) return

            setSystemNote(
                language.getInterpolated(
                    LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_TelSysLoadFailed,
                    describeBulkError(error, language).text
                )
            )
        }
    }

    // Fill the CME list on open, pre-selecting the page's CME (or the only one) and its telsys.
    useEffect((): void => {
        const preferTelSys: Nullable<string> = isConcreteID(initialTelSysId) ? String(initialTelSysId) : null

        getCMEsAsSelectOptions()
            .catch((): SelectOptions<string> => [])
            .then(async (options: SelectOptions<string>): Promise<void> => {
                if (!isMounted.current) return

                const sorted: SelectOptions<string> = [...options].sort(byLabel)
                const hasInitial: boolean = sorted.some(
                    (option: SelectOption<string>): boolean => option.value === initialCmeId
                )

                if (isStringNotEmpty(initialCmeId) && !hasInitial) {
                    sorted.unshift({ label: initialCmeId, value: initialCmeId })
                }

                setCmeOptions(sorted)

                let pick: string = EMPTY_STRING

                if (isStringNotEmpty(initialCmeId)) {
                    pick = initialCmeId
                } else if (sorted.length === 1) {
                    pick = sorted[0].value
                }

                setCmeId(pick)
                await loadSystems(pick, preferTelSys, isStringNotEmpty(initialCmeId) && isVarNotEmpty(preferTelSys))
            })
            .catch((): void => {
                // Errors are reported in the system note.
            })
    }, [])

    const selectCme = useCallback((id: string): void => {
        setCmeId(id)
        loadSystems(id, null, false).catch(ignoreError)
    }, [])

    const selectTelSys = useCallback(
        (id: string): void => {
            const option: Definable<SelectOption<string>> = telSysOptions.find(
                (candidate: SelectOption<string>): boolean => candidate.value === id
            )

            chooseTelSys(id, false, option?.telSysType).catch(ignoreError)
        },
        [telSysOptions]
    )

    const toBulkRow = (item: LicenceManagementListItem, index: number): BulkRow => {
        const key: string = [
            item.TelSysID,
            item.PartIDInTelSys,
            item.EntryType,
            item.DeviceNumber,
            item.Username,
            index
        ].join('|')

        return {
            key,
            item,
            row: mapListItemToRow(item, key, language, isCxiDataEnabled, telSysType, integrationType),
            prefilledEmail: prefilledEmailFor(item),
            exists: isStringNotEmpty(item.Username)
        }
    }

    const loadGroups = async (loaded: readonly BulkRow[], unlicensed: number): Promise<void> => {
        setIsLoadingGroups(true)

        const memberships: Map<string, readonly DeviceOrAgentGroupMembershipRow[]> = new Map()
        const todo: readonly BulkRow[] = loaded.filter(
            (row: BulkRow): boolean => isStringNotEmpty(row.row.DeviceNumber) && isVarNotEmpty(row.item.PartIDInTelSys)
        )

        const failed: number = await runWithConcurrency(
            todo,
            GROUP_LOAD_CONCURRENCY,
            async (row: BulkRow): Promise<void> => {
                memberships.set(
                    row.key,
                    await getDeviceGroupMemberships(
                        row.row.TelSysID || telSysId,
                        row.row.PartIDInTelSys,
                        row.row.DeviceNumber
                    )
                )
            },
            (done: number): void => {
                if (isMounted.current && (done % GROUP_LOAD_CONCURRENCY === 0 || done === todo.length)) {
                    setLoadMessage(
                        language.getInterpolated(
                            LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_LoadingGroups,
                            done,
                            todo.length
                        )
                    )
                }
            }
        )

        if (!isMounted.current) return

        const groupCount: number = buildGroupSummaries(loaded, memberships).size
        const loadedText: string = language.getInterpolated(
            LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_LoadedWithGroups,
            loaded.length,
            unlicensed,
            groupCount
        )

        setGroupsByKey(memberships)
        setIsLoadingGroups(false)
        setLoadMessage(
            failed > 0
                ? `${loadedText} ${language.getInterpolated(
                      LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_GroupFailures,
                      failed
                  )}`
                : loadedText
        )
    }

    const loadUsers = useCallback(async (): Promise<void> => {
        // Rows are mapped with the system's type, so wait until it is known.
        if (isResolvingType) return

        if (IsStringEmpty(telSysId)) {
            setLoadMessage(
                language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_ChooseTelSysFirst)
            )
            return
        }

        resetSystemData()
        setIsLoadingUsers(true)
        setLoadMessage(language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Loading))

        try {
            const items: LicenceManagementListItem[] = []
            let total: number = Number.POSITIVE_INFINITY

            while (items.length < total) {
                // Pages are read one after another: each offset depends on the previous page.
                // eslint-disable-next-line no-await-in-loop
                const response: Nullable<LicenceManagementListResponse> = await getLicenceManagementList(
                    telSysId,
                    ALL_VALUE,
                    cmeId,
                    true,
                    items.length,
                    BULK_LIST_PAGE_SIZE
                )
                const page: readonly LicenceManagementListItem[] = response?.Data?.Items ?? []

                total = response?.Data?.Total ?? 0
                items.push(...page)

                if (isMounted.current) {
                    setLoadMessage(
                        language.getInterpolated(
                            LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_LoadingProgress,
                            items.length,
                            total
                        )
                    )
                }

                if (page.length === 0) break
            }

            if (!isMounted.current) return

            // Rows from other systems are dropped: they would be written with this system's licences.
            const loaded: readonly BulkRow[] = items
                .filter((item: LicenceManagementListItem): boolean => isItemOnTelSys(item, telSysId))
                .map(toBulkRow)
            const unlicensed: number = loaded.filter((row: BulkRow): boolean => !isItemLicensed(row.item)).length

            setRows(loaded)
            setLoadMessage(
                language.getInterpolated(
                    LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Loaded,
                    loaded.length,
                    unlicensed
                )
            )
            await loadGroups(loaded, unlicensed)
        } catch (error) {
            if (!isMounted.current) return

            const status: number = errorStatus(error)

            setLoadMessage(
                language.getInterpolated(
                    status === 401 || status === 403
                        ? LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_LoadFailedSession
                        : LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_LoadFailed,
                    describeBulkError(error, language).text
                )
            )
        } finally {
            if (isMounted.current) setIsLoadingUsers(false)
        }
    }, [telSysId, cmeId, language, isCxiDataEnabled, telSysType, integrationType, isResolvingType])

    const emailFor = useCallback(
        (row: BulkRow): string => (emailOverrides.get(row.key) ?? row.prefilledEmail).trim(),
        [emailOverrides]
    )
    const nameFor = useCallback((row: BulkRow): string => bulkDisplayName(row, emailFor(row)), [emailFor])
    const usernameFor = useCallback((row: BulkRow): string => bulkUsername(row, emailFor(row)), [emailFor])
    const isDone = useCallback((row: BulkRow): boolean => assigned.has(row.key), [assigned])
    const assignedLicence = useCallback((row: BulkRow): Definable<BulkLicence> => assigned.get(row.key), [assigned])
    const blockerFor = useCallback(
        (row: BulkRow): Nullable<BlockerCode> =>
            getBulkBlocker(row, isItemLicensed(row.item) || assigned.has(row.key), emailFor(row), licence, profile),
        [assigned, emailFor, licence, profile]
    )
    const isSelectable = useCallback(
        (row: BulkRow): boolean => !isRunning && !isDone(row) && blockerFor(row) === null,
        [isRunning, isDone, blockerFor]
    )
    const isSelected = useCallback(
        (row: BulkRow): boolean => selected.has(row.key) && blockerFor(row) === null && !isDone(row),
        [selected, blockerFor, isDone]
    )
    const statusFor = useCallback((row: BulkRow): Definable<BulkRowStatus> => statuses.get(row.key), [statuses])

    const visibleRows: readonly BulkRow[] = useMemo((): readonly BulkRow[] => {
        const query: string = search.trim().toLowerCase()

        return rows.filter((row: BulkRow): boolean => {
            if (!showLicensed && isItemLicensed(row.item) && !assigned.has(row.key)) return false

            if (
                selectedGroups.size > 0 &&
                !(groupsByKey.get(row.key) ?? []).some((group: DeviceOrAgentGroupMembershipRow): boolean =>
                    selectedGroups.has(group.ID)
                )
            ) {
                return false
            }

            return matchesBulkSearch(row, bulkDisplayName(row, emailFor(row)), emailFor(row), query)
        })
    }, [rows, showLicensed, assigned, selectedGroups, groupsByKey, search, emailFor])

    const groups: readonly BulkGroupSummary[] = useMemo(
        (): readonly BulkGroupSummary[] =>
            Array.from(buildGroupSummaries(rows, groupsByKey).values()).sort(
                (a: BulkGroupSummary, b: BulkGroupSummary): number => a.label.localeCompare(b.label)
            ),
        [rows, groupsByKey]
    )

    const selectedRows: readonly BulkRow[] = useMemo(
        (): readonly BulkRow[] => rows.filter((row: BulkRow): boolean => isSelected(row)),
        [rows, isSelected]
    )
    const selectableVisible: readonly BulkRow[] = useMemo(
        (): readonly BulkRow[] =>
            visibleRows.filter((row: BulkRow): boolean => !isDone(row) && blockerFor(row) === null),
        [visibleRows, isDone, blockerFor]
    )
    const allVisibleSelected: boolean =
        selectableVisible.length > 0 && selectableVisible.every((row: BulkRow): boolean => selected.has(row.key))

    const toggleRow = useCallback(
        (row: BulkRow): void => {
            if (!isSelectable(row)) return

            setSelected((previous: ReadonlySet<string>): ReadonlySet<string> => {
                const next: Set<string> = new Set(previous)

                if (next.has(row.key)) {
                    next.delete(row.key)
                } else {
                    next.add(row.key)
                }

                return next
            })
        },
        [isSelectable]
    )

    const setAllVisibleSelected = useCallback(
        (checked: boolean): void => {
            setSelected((previous: ReadonlySet<string>): ReadonlySet<string> => {
                const next: Set<string> = new Set(previous)

                selectableVisible.forEach((row: BulkRow): void => {
                    if (checked) {
                        next.add(row.key)
                    } else {
                        next.delete(row.key)
                    }
                })

                return next
            })
        },
        [selectableVisible]
    )

    const setEmail = useCallback((row: BulkRow, email: string): void => {
        setEmailOverrides(
            (previous: ReadonlyMap<string, string>): ReadonlyMap<string, string> =>
                new Map(previous).set(row.key, email)
        )
    }, [])

    const toggleGroup = useCallback((groupID: string): void => {
        setSelectedGroups((previous: ReadonlySet<string>): ReadonlySet<string> => {
            const next: Set<string> = new Set(previous)

            if (next.has(groupID)) {
                next.delete(groupID)
            } else {
                next.add(groupID)
            }

            return next
        })
    }, [])

    const clearGroups = useCallback((): void => setSelectedGroups(new Set()), [])

    const setStatus = (key: string, status: BulkRowStatus): void => {
        if (!isMounted.current) return

        setStatuses(
            (previous: ReadonlyMap<string, BulkRowStatus>): ReadonlyMap<string, BulkRowStatus> =>
                new Map(previous).set(key, status)
        )
    }

    const statusText = (key: string): string => language.get(key)

    /** Creates or updates one row's user. Resolves to a warning, if any. */
    const processRow = async (row: BulkRow, runLicence: BulkLicence): Promise<Nullable<BulkError>> => {
        const seeded: LicenceDraftValues = seedRowLicenceValues(row.row, undefined)
        const draft: LicenceDraftValues = { ...seeded, userLicence: runLicence }

        if (row.exists) {
            const existing: Definable<ApplicationUser> = (await getUser(row.row.Username))?.Data

            if (!existing) {
                throw new Error(language.get(LicenceManagementPageLanguage.LicenceManagementPage_Licence_SaveFailed))
            }

            if (isAdminUser(existing)) throw adminUserError(language)

            const body: AdminUserBody = await buildExistingUserLicenceUpdate(
                row.row,
                existing,
                draft,
                seeded.userLicence,
                integrationType,
                telSysType
            )

            await updateUser(row.row.Username, body)

            return null
        }

        const body: AdminUserBody = await buildCreateUserBody(
            row.row,
            emailFor(row),
            draft,
            welcomeEmail,
            integrationType,
            telSysType
        )

        try {
            await createUser(body)

            return null
        } catch (error) {
            // Saved, but the welcome e-mail could not be sent.
            if (isPartialResult(error)) return describeBulkError(error, language)

            // CCS sometimes saves the user and then returns a 5xx: check before reporting a failure.
            // Only the user this create sent counts, not another user who happens to share the username.
            if (!isServerError(error)) throw error

            let saved: Definable<ApplicationUser>

            try {
                saved = (await getUser(body.Username))?.Data
            } catch {
                saved = undefined
            }

            if (!isSavedAsSent(saved, body)) throw error

            return describeBulkError(error, language)
        }
    }

    /** Runs one row and records its outcome. Resolves to true when it succeeded. */
    const runRow = async (row: BulkRow, runLicence: BulkLicence, label: string): Promise<boolean> => {
        setStatus(row.key, {
            kind: BulkRowStatusKind.WORKING,
            text: statusText(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Status_Working),
            licence: runLicence
        })

        try {
            const warning: Nullable<BulkError> = await processRow(row, runLicence)

            setStatus(row.key, {
                kind: warning ? BulkRowStatusKind.DONE_WARNING : BulkRowStatusKind.DONE,
                text: language.getInterpolated(
                    row.exists
                        ? LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Status_Updated
                        : LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Status_Created,
                    label
                ),
                detail: warning
                    ? {
                          ...warning,
                          text: language.get(
                              LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_CreatedWarning
                          )
                      }
                    : undefined,
                licence: runLicence
            })

            if (isMounted.current) {
                setHasChanges(true)
                setAssigned(
                    (previous: ReadonlyMap<string, BulkLicence>): ReadonlyMap<string, BulkLicence> =>
                        new Map(previous).set(row.key, runLicence)
                )
                setSelected((previous: ReadonlySet<string>): ReadonlySet<string> => {
                    const next: Set<string> = new Set(previous)

                    next.delete(row.key)

                    return next
                })
            }

            return true
        } catch (error) {
            let detail: BulkError = describeBulkError(error, language)

            if (isSessionLoss(error)) {
                stopRef.current = true
                detail = {
                    ...detail,
                    text: language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Error_Session)
                }
            }

            setStatus(row.key, {
                kind: BulkRowStatusKind.FAILED,
                text: statusText(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Status_Failed),
                detail,
                licence: runLicence
            })

            return false
        }
    }

    const execute = async (plan: readonly BulkRow[]): Promise<void> => {
        const runLicence: BulkLicence = licence
        const label: string = licenceLabel(runLicence)
        let succeeded: number = 0
        let failed: number = 0

        stopRef.current = false
        setIsRunning(true)
        setSummary(null)
        setProgress(0)
        setRunCounts({ total: plan.length, succeeded: 0, failed: 0 })

        setStatuses((previous: ReadonlyMap<string, BulkRowStatus>): ReadonlyMap<string, BulkRowStatus> => {
            const next: Map<string, BulkRowStatus> = new Map(previous)

            plan.forEach((row: BulkRow): void => {
                next.set(row.key, {
                    kind: BulkRowStatusKind.WAITING,
                    text: statusText(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Status_Waiting),
                    licence: runLicence
                })
            })

            return next
        })

        for (let index: number = 0; index < plan.length; index += 1) {
            const row: BulkRow = plan[index]

            if (stopRef.current) {
                setStatus(row.key, {
                    kind: BulkRowStatusKind.NOT_RUN,
                    text: statusText(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Status_NotRun),
                    licence: runLicence
                })
            } else {
                // Users are processed one at a time, so Stop and a lost session take effect between them.
                // eslint-disable-next-line no-await-in-loop
                if (await runRow(row, runLicence, label)) {
                    succeeded += 1
                } else {
                    failed += 1
                }

                if (isMounted.current) {
                    setProgress(Math.round(((index + 1) / plan.length) * 100))
                    setRunCounts({ total: plan.length, succeeded, failed })
                }
            }
        }

        if (!isMounted.current) return

        setIsRunning(false)
        setSummary({ succeeded, failed, stopped: stopRef.current })
    }

    const requestRun = useCallback((): void => {
        if (selectedRows.length === 0) return

        setIsConfirmOpen(true)
    }, [selectedRows])

    const confirmRun = useCallback((): void => {
        setIsConfirmOpen(false)
        execute(selectedRows).catch(ignoreError)
    }, [selectedRows, licence, welcomeEmail, integrationType, telSysType, emailFor])

    const cancelConfirm = useCallback((): void => setIsConfirmOpen(false), [])

    const stop = useCallback((): void => {
        stopRef.current = true
    }, [])

    // Leaving the page mid-run stops the run, rather than carrying on with no dialog to show or stop it.
    useEffect(
        (): (() => void) => (): void => {
            stopRef.current = true
        },
        []
    )

    const closeSummary = useCallback((): void => {
        setSummary(null)
        setProgress(0)
        setRunCounts(null)
    }, [])

    const runPlan: BulkRunPlan = useMemo((): BulkRunPlan => {
        const newUsers: number = selectedRows.filter((row: BulkRow): boolean => !row.exists).length
        const remaining: Nullable<number> = remainingLicences(licenceSummary, licence)

        return {
            total: selectedRows.length,
            newUsers,
            existingUsers: selectedRows.length - newUsers,
            remaining,
            shortfall:
                remaining === null || remaining === undefined
                    ? 0
                    : Math.max(0, selectedRows.length - Math.max(0, remaining))
        }
    }, [selectedRows, licenceSummary, licence])

    const hasResults: boolean = statuses.size > 0

    const downloadResults = useCallback((): void => {
        const header: BulkResultLine = [
            language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Csv_Name),
            language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Csv_Username),
            language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Csv_Device),
            language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Csv_Agent),
            language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Csv_Partition),
            language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Csv_Licence),
            language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Csv_Action),
            language.get(LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Csv_Result)
        ]
        const lines: BulkResultLine[] = rows
            .filter((row: BulkRow): boolean => statuses.has(row.key))
            .map((row: BulkRow): BulkResultLine => {
                const status: BulkRowStatus = statuses.get(row.key) as BulkRowStatus
                const detail: string = status.detail
                    ? `: ${status.detail.text}${
                          isStringNotEmpty(status.detail.meta) ? ` (${status.detail.meta})` : EMPTY_STRING
                      }`
                    : EMPTY_STRING

                return [
                    nameFor(row),
                    usernameFor(row),
                    row.row.DeviceNumber,
                    row.row.AgentNumber,
                    row.row.PartitionDescription,
                    licenceLabel(status.licence),
                    language.get(
                        row.exists
                            ? LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Action_Update
                            : LicenceManagementPageLanguage.LicenceManagementPage_BulkAssign_Action_Create
                    ),
                    `${status.text}${detail}`
                ]
            })

        downloadCsv(buildResultsCsv(header, lines), resultsCsvFileName(new Date()))
    }, [language, rows, statuses, nameFor, usernameFor, licenceLabel])

    return {
        cmeOptions,
        cmeId,
        telSysOptions,
        telSysId,
        profile,
        systemNote,
        isDetected,
        rows,
        visibleRows,
        isLoadingUsers,
        isLoadingGroups,
        isResolvingType,
        loadMessage,
        groups,
        selectedGroups,
        groupSearch,
        search,
        showLicensed,
        licence,
        welcomeEmail,
        isRunning,
        progress,
        runCounts,
        isConfirmOpen,
        summary,
        hasChanges,
        selectedRows,
        allVisibleSelected,
        anyVisibleSelectable: selectableVisible.length > 0,
        hasResults,
        runPlan,
        emailFor,
        nameFor,
        usernameFor,
        blockerFor,
        isDone,
        isSelected,
        isSelectable,
        statusFor,
        licenceLabel,
        assignedLicence,
        selectCme,
        selectTelSys,
        loadUsers,
        setSearch,
        setShowLicensed,
        setGroupSearch,
        toggleGroup,
        clearGroups,
        setLicence,
        setWelcomeEmail,
        toggleRow,
        setAllVisibleSelected,
        setEmail,
        requestRun,
        confirmRun,
        cancelConfirm,
        stop,
        closeSummary,
        downloadResults
    }
}
